import React, { useEffect } from "react";
import { BadgeDialogContent } from "./helpers";

interface Props {
  content: BadgeDialogContent | null;
  open: boolean;
  onClose: () => void;
}

const BadgeDialog: React.FC<Props> = ({ content, open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!content) return null;

  const { earned, title, message, imageUrl, courseName, enrolHref, enrolLabel } =
    content;

  return (
    <div
      className={`badge-backdrop ${open ? "is-open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="badgeTitle"
      aria-describedby="badgeMessage"
      aria-hidden={!open}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={`badge-modal ${earned ? "is-awarded" : "is-locked"}`}>
        <button
          type="button"
          className="badge-close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg
            viewBox="0 0 14 14"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="2" y1="2" x2="12" y2="12" />
            <line x1="12" y1="2" x2="2" y2="12" />
          </svg>
        </button>

        <span className="badge-eyebrow">
          {earned ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
              <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <rect
                x="3"
                y="7"
                width="10"
                height="7"
                rx="1.5"
                fill="currentColor"
                opacity=".9"
              />
              <path
                d="M5.5 7 V5 a2.5 2.5 0 0 1 5 0 V7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              />
            </svg>
          )}
          {earned ? "Earned" : "Not yet earned"}
        </span>

        <div className="badge-hero">
          <div className="badge-bigdisc">
            {imageUrl ? (
              <img src={imageUrl} alt="" />
            ) : (
              <span className="ph-mark">Locked</span>
            )}
          </div>
          <div className="badge-padlock" aria-hidden="true">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <rect
                x="3"
                y="7"
                width="10"
                height="7"
                rx="1.5"
                fill="currentColor"
                opacity=".9"
              />
              <path
                d="M5.5 7 V5 a2.5 2.5 0 0 1 5 0 V7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              />
            </svg>
          </div>
        </div>

        <h2 className="badge-title" id="badgeTitle">
          {title}
        </h2>

        {courseName && <p className="badge-course">{courseName}</p>}

        <p className="badge-message" id="badgeMessage">
          {message}
        </p>

        <div className="badge-foot">
          {enrolHref && (
            <a href={enrolHref} className="btn btn-blue">
              {enrolLabel}
            </a>
          )}
          <button type="button" className="btn btn-outline" onClick={onClose}>
            {earned ? "Nice one" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadgeDialog;
